import { useCallback, useEffect, useState } from "react";
import type { ChangeEvent, ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Notiz } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { relativeZeit } from "../format.js";
import { renderMarkdown } from "../markdown.js";
import "./KundeNotizen.css";

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test
 *  mit einer Handvoll vi.fn zeichnen (Muster aus KundeAnsprechpartner.tsx). */
export type NotizenClient = Pick<ApiClient,
  "listNotes" | "createNote" | "updateNote" | "deleteNote">;

/** Die Verdrahtung, die von App über KundenAkte hierher durchgereicht wird (erst in T11). */
export type NotizenUmfeld = {
  client: NotizenClient;
  /** Id des angemeldeten Benutzers — entscheidet zusammen mit `istAdmin`, wer eine Notiz
   *  bearbeiten/löschen darf (der API-Wächter sitzt zusätzlich in der Route). */
  angemeldeterBenutzerId: string;
  istAdmin: boolean;
};

export type KundeNotizenProps = NotizenUmfeld & { kundeId: string };

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (fehler instanceof ApiError && fehler.message.trim() !== "") return fehler.message;
  return standard;
}

/**
 * Der Notiz-Reiter: ein Journal (neueste oben) mit gerendertem Markdown je Eintrag. Eine
 * eigene Notiz (oder jede, wenn ADMIN) trägt Bearbeiten/Löschen; Ändern schickt die gelesene
 * Version als optimistische Sperre mit. Der Inhalt läuft IMMER durch den sanitisierenden
 * Renderer (renderMarkdown) — nie als rohes HTML.
 *
 * Anlegen und Bearbeiten laufen im strikten Modalpanel (Hausregel, Entscheid Dirk vom
 * 06.09.2026). Vorher standen sie als offene Kästen im Journal: Ein Klick auf „Bearbeiten"
 * an einer anderen Notiz warf den halb getippten Text weg, und ein Wechsel des Reiters
 * ebenso — beides ohne Rückfrage. Genau davor schützt der Rahmen mit seinen zwei Ausgängen.
 *
 * Eigene Wurzelklasse `.kn` (NICHT `.ka` teilen — Kollisions-Lehre 01.09., siehe `.ka-neu`
 * in KundeAnlegen).
 */
export function KundeNotizen(p: KundeNotizenProps): ReactElement {
  const { client, kundeId, angemeldeterBenutzerId, istAdmin } = p;

  const [notizen, setNotizen] = useState<Notiz[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  // null = kein Anlege-Feld offen; eine Zeichenkette = das Feld ist offen mit diesem Entwurf.
  const [anlegenEntwurf, setAnlegenEntwurf] = useState<string | null>(null);
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null);
  const [bearbeiteEntwurf, setBearbeiteEntwurf] = useState("");
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const { items } = await client.listNotes(kundeId);
      setNotizen(items);
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Notizen konnten nicht geladen werden."));
    }
  }, [client, kundeId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  function darfBearbeiten(n: Notiz): boolean {
    return n.authorUserId === angemeldeterBenutzerId || istAdmin;
  }

  async function anlegen(): Promise<void> {
    if (anlegenEntwurf === null || anlegenEntwurf.trim() === "" || speichert) return;
    setSpeichert(true);
    try {
      await client.createNote(kundeId, { contentMarkdown: anlegenEntwurf });
      setAnlegenEntwurf(null);
      setHinweis(null);
      await laden();
    } catch (e) {
      setHinweis(alsMeldung(e, "Die Notiz konnte nicht gespeichert werden."));
    } finally {
      setSpeichert(false);
    }
  }

  function bearbeitenStarten(n: Notiz): void {
    setBearbeiteId(n.id);
    setBearbeiteEntwurf(n.contentMarkdown);
    setHinweis(null);
  }

  async function aendernSpeichern(n: Notiz): Promise<void> {
    if (bearbeiteEntwurf.trim() === "" || speichert) return;
    setSpeichert(true);
    try {
      // expectedVersion = die gelesene Version: Ist der Datensatz zwischenzeitlich fremd
      // geändert worden, antwortet die API mit 409 statt still zu überschreiben.
      await client.updateNote(kundeId, n.id, n.version, { contentMarkdown: bearbeiteEntwurf });
      setBearbeiteId(null);
      setHinweis(null);
      await laden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setHinweis("Diese Notiz wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen — bitte erneut bearbeiten.");
        setBearbeiteId(null);
        await laden();
      } else {
        setHinweis(alsMeldung(e, "Die Änderung konnte nicht gespeichert werden."));
      }
    } finally {
      setSpeichert(false);
    }
  }

  async function loeschen(n: Notiz): Promise<void> {
    if (!window.confirm(`Diese Notiz von ${n.authorName} wirklich löschen?`)) return;
    try {
      await client.deleteNote(kundeId, n.id);
      setHinweis(null);
      await laden();
    } catch (e) {
      setHinweis(alsMeldung(e, "Die Notiz konnte nicht gelöscht werden."));
    }
  }

  // ISO-Zeitstempel sortieren lexikografisch = chronologisch → neueste zuerst.
  const sortiert = [...(notizen ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  /**
   * Die Notiz, die gerade bearbeitet wird — Quelle für den Vergleich, die Version UND für
   * die Frage, ob eine Maske offen steht.
   *
   * Die EINE Quelle ist Absicht (Fixrunde 1, 07.09.2026): Sperrte der Knopf „Neue Notiz"
   * sich an `bearbeiteId`, während das Panel an dieser Notiz hängt, gäbe es ein Fenster, in
   * dem beides auseinanderfällt — eine Kennung, zu der die Liste (nach einem Neuladen, nach
   * einem fremden Löschen) keine Notiz mehr führt. Dann stünde kein Panel, aber der Knopf
   * bliebe stillgelegt: eine Sackgasse, aus der nur der Reiterwechsel hilft.
   */
  const bearbeitete = sortiert.find((n) => n.id === bearbeiteId) ?? null;

  /**
   * Der EINE Ausgang der beiden Masken neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage, sobald
   * etwas Ungespeichertes darin steht.
   *
   * „Etwas darin" heisst beim ANLEGEN: irgendein Text (der Stand beim Öffnen ist leer);
   * beim BEARBEITEN: ein anderer Text als der gespeicherte. Ein eigener Ausgangsstand wird
   * dafür nicht mitgeführt — die Notiz selbst ist er.
   */
  function anlegenAbbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen((anlegenEntwurf ?? "").trim() !== "")) return;
    setAnlegenEntwurf(null);
  }

  function bearbeitenAbbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(bearbeitete !== null
      && bearbeiteEntwurf !== bearbeitete.contentMarkdown)) return;
    setBearbeiteId(null);
  }

  /** Steht gerade eine der beiden Masken offen? */
  const maskeOffen = anlegenEntwurf !== null || bearbeitete !== null;

  /** Die Rückmeldung eines Schreibwegs — im Panel, solange eines steht. */
  function hinweisband(): ReactElement | null {
    if (hinweis === null) return null;
    return <p className="kn__meldung" role="status">{hinweis}</p>;
  }

  /** Die Vorschau unter dem Feld — dieselbe in beiden Masken, damit sie nicht auseinander
   *  laufen. Sie steht nur bei Inhalt: Eine Überschrift „Vorschau" über nichts ist eine
   *  Zeile, die nur Platz kostet. */
  function vorschau(text: string): ReactElement | null {
    if (text.trim() === "") return null;
    return (
      <div className="kn__vorschau">
        <span className="kn__vorschau-label">Vorschau</span>
        {renderMarkdown(text)}
      </div>
    );
  }

  return (
    <div className="kn">
      <div className="kn__neu-bereich">
        <button type="button" className="kn__neu" onClick={() => setAnlegenEntwurf("")}
                disabled={maskeOffen}>
          Neue Notiz
        </button>
      </div>

      {anlegenEntwurf !== null && (
        <Modal titel="Neue Notiz" breite="mittel"
          onAbbrechen={anlegenAbbrechen} abbrechenGesperrt={speichert}>
          <div className="kn__editor">
            <label className="kn__editor-label" htmlFor="kn-neu-feld">
              Neue Notiz (Markdown)
            </label>
            <textarea
              id="kn-neu-feld"
              className="kn__textarea"
              value={anlegenEntwurf}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setAnlegenEntwurf(e.target.value)}
              rows={8}
            />
            {vorschau(anlegenEntwurf)}
            {hinweisband()}
          </div>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={anlegenEntwurf.trim() === "" || speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void anlegen(); }} onAbbrechen={anlegenAbbrechen} />
        </Modal>
      )}

      {bearbeitete !== null && (
        <Modal titel="Notiz bearbeiten" breite="mittel"
          onAbbrechen={bearbeitenAbbrechen} abbrechenGesperrt={speichert}>
          <div className="kn__editor">
            {/* „Notiz (Markdown)" und nicht mehr „Notiz bearbeiten": Der Panel-Titel sagt
                bereits, dass hier bearbeitet wird — trüge das Feld denselben Namen, gäbe es
                zwei Elemente mit gleichem zugänglichen Namen (Panel und Feld), und eine
                Suche danach träfe beide. Der Feldname sagt jetzt, WAS darin steht. */}
            <label className="kn__editor-label" htmlFor="kn-bearbeiten-feld">
              Notiz (Markdown)
            </label>
            <textarea
              id="kn-bearbeiten-feld"
              className="kn__textarea"
              value={bearbeiteEntwurf}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setBearbeiteEntwurf(e.target.value)}
              rows={8}
            />
            {vorschau(bearbeiteEntwurf)}
            {hinweisband()}
          </div>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={bearbeiteEntwurf.trim() === "" || speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void aendernSpeichern(bearbeitete); }}
            onAbbrechen={bearbeitenAbbrechen} />
        </Modal>
      )}

      {fehler !== null && <p className="kn__meldung" role="alert">{fehler}</p>}
      {/* Der Hinweis steht dort, wo gerade gearbeitet wird: bei offener Maske IM Panel
          (siehe oben), sonst hier. Hinter dem Schleier wäre die Auskunft „konnte nicht
          gespeichert werden" sichtbar, aber nicht zu befolgen — der Text, um den es geht,
          läge davor. */}
      {!maskeOffen && hinweis !== null && (
        <p className="kn__meldung" role="status">{hinweis}</p>
      )}

      {notizen !== null && sortiert.length === 0 && (
        <p className="kn__leer">Noch keine Notizen zu diesem Kunden.</p>
      )}

      {/* Das Journal bleibt IMMER lesbar — auch hinter einem offenen Panel: Es ist der
          Zusammenhang, in dem gerade geschrieben wird. Der Eintrag zeigt dabei weiterhin
          seinen gespeicherten Stand, nicht den Entwurf; was im Panel steht, ist noch
          nicht die Notiz.

          Seine AKTIONEN sind dabei gesperrt (`maskeOffen`, Fixrunde 1 vom 07.09.2026): Sie
          liegen hinter dem Schleier und wären Knöpfe, die niemand drücken kann. Der Riegel
          sagt das nicht nur — er hält auch die Tests ehrlich: In jsdom fängt kein Element
          Zeigerereignisse ab, ein Klick „hinter" das Panel ginge dort durch und liesse eine
          klemmende Maske grün aussehen. */}
      <ul className="kn__journal">
        {sortiert.map((n) => (
          <li key={n.id} className="kn__eintrag">
            <div className="kn__kopf">
              <span className="kn__autor">{n.authorName}</span>
              <span className="kn__zeit">
                {relativeZeit(n.createdAt)}
                {n.updatedAt !== n.createdAt && <span className="kn__geaendert"> · bearbeitet</span>}
              </span>
            </div>

            <div className="kn__inhalt">{renderMarkdown(n.contentMarkdown)}</div>

            {darfBearbeiten(n) && (
              <div className="kn__aktionen">
                <button type="button" className="kn__aktion" disabled={maskeOffen}
                        onClick={() => bearbeitenStarten(n)}>
                  Bearbeiten
                </button>
                <button type="button" className="kn__aktion kn__aktion--gefahr"
                        disabled={maskeOffen}
                        onClick={() => void loeschen(n)}>
                  Löschen
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
